import React from 'react';
import { Controller } from 'react-hook-form';
import {
  Table,
  useTableFilterSortData,
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import {
  Box,
  NumericInput,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  AlertIcon,
  MoneyIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { useLocalStorage } from '@fortress-technology-solutions/fortress-component-library/hooks';
import useBaseTableProps from '../../../hooks/useBaseTableProps';
import useUniqueTableName from '../../../hooks/useUniqueTableName';
import useIntl from '../../../hooks/useIntl';
import rentForecastMessages from '../messages';
import messages from './messages';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import {
  useForecastRentsUtilityAllowanceFormQuery,
  useProposedUtilityAllowanceValues,
} from './hooks';
import { defaultColumnWidths } from './constants';
import { getAggregatedProgramTypes } from '../../../utils/affordable';
import { calculateWithProposed } from '../utils';
import { factorToPercent } from '../hooks.useRows';

const TableInput = ({
  stateKey,
  rowId,
  inputProps,
  useFormReturn,
  adornment,
}) => {
  const intl = useIntl();

  const name = `${rowId}-${stateKey}`;

  const rules = React.useMemo(
    () => ({
      validate: (value) => {
        // Skip validation for undefined, null, or empty string values
        if (value === undefined || value === null || value === '') {
          return true;
        }

        // Convert to number for validation
        const numValue = parseFloat(value);

        // Allow negative values only for 'proposedChange' column
        if (stateKey !== 'proposedChange' && numValue < 0) {
          return stateKey === 'electric'
            ? intl.formatMessage(messages.utilityAllowanceTableElectricError)
            : false;
        }

        return true; // No error for valid values
      },
    }),
    [stateKey, intl],
  );

  return (
    <>
      <Controller
        control={useFormReturn.control}
        name={name}
        rules={rules}
        render={({ field, fieldState }) => {
          const { value, onChange } = field;
          const error = fieldState.error;
          const inputElement = (
            <NumericInput
              value={value}
              onChange={(e) => {
                // prevent negative numbers from being entered (except for proposedChange)
                // prevent non-numeric characters from being entered
                // prevent more than 2 decimal places from being entered
                const value = e.target.value;
                const allowNegative = stateKey === 'proposedChange';
                const sanitizedValue = value.replace(
                  allowNegative ? /[^0-9\\.\\-]/g : /[^0-9\\.]/g,
                  '',
                );

                if (!allowNegative && sanitizedValue.startsWith('-')) {
                  return;
                }

                const regex = allowNegative
                  ? /^-?\d*\.?\d{0,2}$/ // Allows negative numbers with up to 2 decimal places
                  : /^\d*\.?\d{0,2}$/; // Allows only positive numbers with up to 2 decimal places

                if (!regex.test(sanitizedValue)) {
                  return;
                }
                onChange(sanitizedValue);
              }}
              error={!!error}
              step="0.01"
              min={
                stateKey !== 'electric' && stateKey !== 'proposedChange'
                  ? 0
                  : undefined
              }
              startAdornment={adornment}
              endAdornment={
                stateKey === 'electric' && error ? (
                  <AlertIcon color="error" />
                ) : null
              }
              HelperTextProps={{
                sx: { display: 'none' },
              }}
              sx={{
                paddingRight: 1,
                '& .MuiInputBase-root.MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
                  {
                    borderColor: 'error.main',
                  },
              }}
              placeholder="0.00"
              {...inputProps}
            />
          );
          if (error) {
            return (
              <>
                <Tooltip
                  title={
                    <Typography variant={'bodySmall'} color={'inherit'}>
                      {error.message}
                    </Typography>
                  }
                  variant="light"
                  placement="top"
                >
                  {inputElement}
                </Tooltip>
              </>
            );
          } else {
            return inputElement;
          }
        }}
      />
    </>
  );
};

const UtilityAllowanceTable = (props) => {
  const {
    useFormReturn,
    rentForecasts, // from API query
    currentRentForecast,
    rentForecastTableRows,
    canEdit,
  } = props;
  const intl = useIntl();

  const { setValue } = useFormReturn;

  const proposedUtilityAllowanceValues = useProposedUtilityAllowanceValues(
    useFormReturn.control,
    rentForecastTableRows,
  );

  const tableName = useUniqueTableName('forecast-rent-utility-allowance');

  const { localStorageState: columnWidths, setLocalStorage: setColumnWidths } =
    useLocalStorage(
      `${tableName}_all-column-widths`,
      'object',
      defaultColumnWidths,
    );
  React.useEffect(() => {
    setColumnWidths({
      ...defaultColumnWidths,
      ...columnWidths,
    });
  }, [setColumnWidths]);

  const [inputData, setInputData] = React.useState({});

  const selectedProperty = useSelectedProperty();

  const { isRDFloorPlan } = getAggregatedProgramTypes(
    rentForecasts?.floorPlans,
  );

  const headers = React.useMemo(
    () => [
      {
        id: 'floorPlan',
        label: intl.formatMessage(rentForecastMessages.floorPlans),
        sortable: true,
      },
      {
        id: 'bedsBaths',
        label: intl.formatMessage(rentForecastMessages.bedsBaths),
        sortable: true,
      },

      ...(isRDFloorPlan
        ? [
            {
              id: 'unitType',
              label: intl.formatMessage(rentForecastMessages.unitType),
              sortable: true,
            },
          ]
        : []),
      {
        id: 'numUnits',
        label: intl.formatMessage(rentForecastMessages.numUnits),
        sortable: true,
      },
      {
        id: 'electric',
        label: intl.formatMessage(
          messages.utilitAllowanceTableColumnTitleElectric,
        ),
        info: intl.formatMessage(
          messages.utilityAllowanceTableElectricColumnTooltip,
        ),
        sortable: true,
      },
      {
        id: 'gas',
        label: intl.formatMessage(messages.utilitAllowanceTableColumnTitleGas),
        sortable: true,
      },
      {
        id: 'water',
        label: intl.formatMessage(
          messages.utilitAllowanceTableColumnTitleWater,
        ),
        sortable: true,
      },
      {
        id: 'sewer',
        label: intl.formatMessage(
          messages.utilitAllowanceTableColumnTitleSewer,
        ),
        sortable: true,
      },
      {
        id: 'trash',
        label: intl.formatMessage(
          messages.utilitAllowanceTableColumnTitleTrash,
        ),
        sortable: true,
      },
      {
        id: 'other',
        label: intl.formatMessage(
          messages.utilitAllowanceTableColumnTitleOther,
        ),
        sortable: true,
      },
      {
        id: 'totalCurrentUtilityAllowance',
        label: intl.formatMessage(
          messages.utilityAllowanceTableTotalCurrentUtilityAllowance,
        ),
        info: intl.formatMessage(
          messages.utilityAllowanceTableTotalCurrentUtilityAllowanceTooltip,
        ),
        sortable: true,
      },
      {
        id: 'proposedChangeUtilityAllowance',
        label: intl.formatMessage(
          messages.utilityAllowanceTableProposedChanged,
        ),
        sortable: true,
      },
      {
        id: 'totalProposedUtilityAllowance',
        label: intl.formatMessage(
          messages.utilityAllowanceTableTotalProposedUtilityAllowance,
        ),
        info: intl.formatMessage(
          messages.utilityAllowanceTableTotalProposedUtilityAllowanceTooltip,
        ),
        sortable: true,
      },
    ],
    [intl],
  );

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name: tableName,
    headers,
  });

  const rawTableData = rentForecasts?.floorPlans?.map((floorPlan) => {
    return {
      floorPlan: floorPlan.internalName,
      floorPlanLink: `/property/${selectedProperty.id}/floorPlan/${floorPlan.id}`,
      bedsBaths: `${floorPlan.nBeds || 0} / ${floorPlan.nBaths || 0}`,
      unitType: isRDFloorPlan ? floorPlan?.unitTypeDetails?.name : null,
      isRDFloorPlan,
      numUnits: floorPlan?.floorplanUnitCount?.numTotalUnits || 0,
      id: floorPlan.id,
      electric: '$0.00',
      gas: '$0.00',
      water: '$0.00',
      sewer: '$0.00',
      trash: '$0.00',
      totalCurrentUtilityAllowance: '$0.00',
      totalProposedUtilityAllowance: '$0.00',
    };
  });

  const sortedAndFilteredResults = useTableFilterSortData({
    results: rawTableData,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
  });

  const onTableInputChange = React.useCallback((stateKey, value, rowId) => {
    setInputData((prevState) => ({
      ...prevState,
      [`${rowId}-${stateKey}`]: value,
    }));
  }, []);

  const rows = React.useMemo(() => {
    if (!sortedAndFilteredResults) return [];

    const getTableInputElement = ({
      stateKey,
      rowId,
      inputProps,
      adornment = <MoneyIcon />,
    }) => {
      return (
        <TableInput
          useFormReturn={useFormReturn}
          stateKey={stateKey}
          tableInputState={inputData}
          onChange={onTableInputChange}
          rowId={rowId}
          inputProps={{
            disabled: !canEdit,
            ...inputProps,
          }}
          adornment={adornment}
        />
      );
    };

    return sortedAndFilteredResults.map((row) => {
      const floorPlanLink = `/property/${selectedProperty.id}/floorPlan/${row.id}`;

      // set total current and proposed ua amounts from rent forecast table rows
      const rentForecastFloorPlanRow =
        rentForecastTableRows.find(
          (rentForecastFloorplanRow) => rentForecastFloorplanRow.id === row.id,
        ) || {};
      const currentUtilityAllowance =
        rentForecastFloorPlanRow['currentRD Utility Allowance']?.value;
      const totalCurrentUtilityAllowance = currentUtilityAllowance || 0;
      const proposedAllowanceChange =
        parseFloat(
          proposedUtilityAllowanceValues[`${row.id}-proposedChange`],
        ) || 0;
      const totalProposedUtilityAllowance = calculateWithProposed(
        proposedAllowanceChange,
        totalCurrentUtilityAllowance,
        currentRentForecast.proposedChangeType,
      );
      return {
        id: row.id,
        floorPlan: {
          variant: 'link',
          to: floorPlanLink,
          value: row.floorPlan,
        },
        bedsBaths: {
          value: row.bedsBaths,
        },
        unitType: {
          value: row?.unitType,
        },
        numUnits: {
          value: row.numUnits,
          variant: 'number',
        },
        electric: {
          value: getTableInputElement({
            stateKey: 'electric',
            rowId: row.id,
            inputProps: { disabled: true },
          }),
        },
        gas: {
          value: getTableInputElement({ stateKey: 'gas', rowId: row.id }),
        },
        water: {
          value: getTableInputElement({ stateKey: 'water', rowId: row.id }),
        },
        sewer: {
          value: getTableInputElement({ stateKey: 'sewer', rowId: row.id }),
        },
        trash: {
          value: getTableInputElement({ stateKey: 'trash', rowId: row.id }),
        },
        other: {
          value: getTableInputElement({ stateKey: 'other', rowId: row.id }),
        },
        totalCurrentUtilityAllowance: {
          value: totalCurrentUtilityAllowance,
          variant: 'currency',
        },
        proposedChangeUtilityAllowance: {
          value: getTableInputElement({
            stateKey: 'proposedChange',
            rowId: row.id,
            adornment:
              currentRentForecast.proposedChangeType === 'percent' ? (
                <Box sx={{ paddingLeft: 1 }}>%</Box>
              ) : (
                <MoneyIcon />
              ),
          }),
        },
        totalProposedUtilityAllowance: {
          value: totalProposedUtilityAllowance,
          variant: 'currency',
        },
      };
    });
  }, [
    sortedAndFilteredResults,
    rentForecastTableRows,
    currentRentForecast?.proposedChangeType,
    proposedUtilityAllowanceValues,
  ]);

  const footerRow = React.useMemo(() => {
    return {
      floorPlan: '',
      bedsBaths: '',
      unitType: '',
      numUnits: {
        value: rows.reduce((acc, row) => acc + row.numUnits.value, 0),
        variant: 'number',
      },
      totalCurrentUtilityAllowance: {
        value: rows.reduce((acc, row) => {
          return acc + (row.totalCurrentUtilityAllowance.value || 0);
        }, 0),
        variant: 'currency',
      },
      totalProposedUtilityAllowance: {
        value: rows.reduce(
          (acc, row) => acc + (row.totalProposedUtilityAllowance.value || 0),
          0,
        ),
        variant: 'currency',
      },
    };
  }, [rows]);

  const baseTableProps = useBaseTableProps();

  const tableManageColumnsProps = useTableManageColumns({
    name: tableName,
    headers,
    firstRow: rows[0],
  });

  // electric value should be totalProposed - sum of other utilities
  const [previousFormValues, setPreviousFormValues] = React.useState({});
  React.useEffect(() => {
    const subscription = useFormReturn.watch((formValues) => {
      const changedFields = Object.keys(formValues).filter(
        (key) => formValues[key] !== previousFormValues[key],
      );
      if (changedFields.length === 0) {
        return;
      }
      const updates = changedFields.reduce((acc, field) => {
        // 732deaee-ea00-4f00-9098-aafa95c9b5cc-gas
        const fieldNamePartsRegexp = /^(.*)-(\w+)$/;
        const floorPlanId = field.match(fieldNamePartsRegexp)[1];
        const floorPlanRow = rows.find((row) => row.id === floorPlanId);
        const proposed =
          parseFloat(
            useFormReturn.getValues(`${floorPlanRow.id}-proposedChange`),
          ) || 0;
        const current = floorPlanRow?.totalCurrentUtilityAllowance?.value || 0;
        const totalProposed = calculateWithProposed(
          proposed,
          current,
          currentRentForecast.proposedChangeType,
        );
        const sumOfOtherUtilities = Object.keys(formValues)
          .filter((key) => key.startsWith(floorPlanId))
          .reduce((acc, value) => {
            const _columnName = value.match(fieldNamePartsRegexp)[2];
            if (!['electric', 'proposedChange'].includes(_columnName)) {
              const rowValue = parseFloat(formValues[value]);
              if (rowValue) {
                return acc + rowValue;
              }
            }
            return acc;
          }, 0);
        const electricValue =
          Math.round((totalProposed - sumOfOtherUtilities) * 100) / 100;
        const electricFieldName = `${floorPlanId}-electric`;
        return {
          ...acc,
          [electricFieldName]: electricValue,
        };
      }, {});

      if (Object.keys(updates).length > 0) {
        Object.keys(updates).forEach((key) => {
          setValue(key, updates[key], {
            shouldValidate: true,
            shouldDirty: false,
            shouldTouch: false,
          });
        });
      }

      setPreviousFormValues({
        ...formValues,
        ...updates, // prevent double render
      });
    });

    return () => subscription.unsubscribe();
  }, [
    useFormReturn.watch,
    setValue,
    currentRentForecast?.proposedChangeType,
    rows,
  ]);

  // trigger electric field recalculation when rows first load
  const currentForecastId = currentRentForecast?.id;
  const electricResetRef = React.useRef(null);

  React.useEffect(() => {
    if (!currentForecastId || electricResetRef.current === currentForecastId)
      return;

    electricResetRef.current = currentForecastId;

    const timeoutId = setTimeout(() => {
      sortedAndFilteredResults?.forEach((row) => {
        const electricFieldName = `${row.id}-electric`;
        setValue(electricFieldName, 0, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [currentForecastId, sortedAndFilteredResults, setValue]);

  const { results: forecastRentsUtilityAllowanceForm, isFetching } =
    useForecastRentsUtilityAllowanceFormQuery(currentRentForecast?.id);

  const forecastId = currentRentForecast?.id;
  const proposedChangeType = currentRentForecast?.proposedChangeType;
  const formDataRef = React.useRef(null);

  const formDataFromAPI = forecastRentsUtilityAllowanceForm?.formData;

  React.useEffect(() => {
    if (!forecastId) return;

    const currentDataKey = `${forecastId}-${JSON.stringify(formDataFromAPI)}`;
    if (formDataRef.current === currentDataKey) return;

    formDataRef.current = currentDataKey;

    const timeoutId = setTimeout(() => {
      if (formDataFromAPI) {
        const formDataForCurrentForecast = { ...formDataFromAPI };

        rentForecastTableRows.forEach((row) => {
          const value =
            row['newRD Utility AllowanceProposedChangeValue']?.value || 0;

          formDataForCurrentForecast[`${row.id}-proposedChange`] =
            proposedChangeType === 'percent' ? factorToPercent(value) : value;
        });

        useFormReturn.reset(formDataForCurrentForecast);
      } else {
        const initialFormData = {};
        rentForecastTableRows.forEach((row) => {
          const value =
            row['newRD Utility AllowanceProposedChangeValue']?.value || 0;

          initialFormData[`${row.id}-proposedChange`] =
            proposedChangeType === 'percent' ? factorToPercent(value) : value;
        });

        useFormReturn.reset(initialFormData);
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [forecastId, formDataFromAPI, proposedChangeType]);
  return (
    <>
      <Table
        {...baseTableProps}
        {...tableManageColumnsProps}
        count={rows?.length}
        totalCount={rows?.length}
        headers={headers}
        height={500}
        intl={intl}
        name={tableName}
        rows={rows}
        footerRow={footerRow}
        handleDateSubmit={handleDateSubmit}
        handleFilterChange={handleFilterChange}
        handleFilterTypeChange={handleFilterTypeChange}
        handleSearchSubmit={handleSearchSubmit}
        handleSortChange={handleSortChange}
        showSettings={false}
        isLoading={isFetching}
      />
    </>
  );
};

export default UtilityAllowanceTable;
